export interface IndividualResultAthlete {
  athlete: string
  club: string
  bibno: number
  score: number
  agegroup: string
  gender: string
  points: number
}

export interface IndividualResultsResponse {
  timestamp: string
  data: {
    results: Record<string, IndividualResultAthlete[]>
    compDates: Record<string, string>
  }
}

export interface AthleteRow {
  key: string
  athlete: string
  club: string
  agegroup: string
  gender: string
  pointsByComp: Record<string, number | undefined>
  totalPoints: number
  scoresByComp: Record<string, number | undefined>
  totalScore: number
}

const apiHost = import.meta.env.VITE_APP_API_HOST || 'https://live.entry4sports.com'

export async function fetchIndividualResults(
  eventGroupId: number,
  eventId?: number
): Promise<IndividualResultsResponse> {
  // /api/results/series/:seriesId → EA
  // /api/results/series/:seriesId/:eventId → EA filtered by event
  //  /api/aai/results/series/:seriesId → AAI

  // const url = `${apiHost}/api/individual-results/series/${eventGroupId}`
  const url = eventId
    ? `${apiHost}/api/results/series/${eventGroupId}/${eventId}`
    : `${apiHost}/api/results/series/${eventGroupId}`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`)
  }

  return response.json()
}

function buildCompositeKey(athlete: IndividualResultAthlete): string {
  return `${athlete.athlete}|${athlete.club}|${athlete.agegroup}|${athlete.gender}`
}

export function processIndividualResults(
  results: Record<string, IndividualResultAthlete[]>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  compDates: Record<string, string>
): AthleteRow[] {
  const compIds = Object.keys(results)
  const athleteMap = new Map<string, AthleteRow>()

  for (const compId of compIds) {
    const athletes = results[compId]
    for (const athlete of athletes) {
      const key = buildCompositeKey(athlete)
      if (!athleteMap.has(key)) {
        athleteMap.set(key, {
          key,
          athlete: athlete.athlete,
          club: athlete.club,
          agegroup: athlete.agegroup,
          gender: athlete.gender,
          pointsByComp: {},
          totalPoints: 0,
          scoresByComp: {},
          totalScore: 0
        })
      }

      const row = athleteMap.get(key)!
      row.pointsByComp[compId] = athlete.points
      row.scoresByComp[compId] = athlete.score
    }
  }

  const rows = Array.from(athleteMap.values())

  for (const row of rows) {
    let totalPoints = 0
    let totalScore = 0
    for (const pts of Object.values(row.pointsByComp)) {
      if (pts !== undefined) {
        totalPoints += pts
      }
    }
    for (const score of Object.values(row.scoresByComp)) {
      if (score !== undefined) {
        totalScore += score
      }
    }
    row.totalPoints = totalPoints
    row.totalScore = totalScore
  }

  rows.sort((a, b) => {
    if (b.totalPoints !== a.totalPoints) {
      return b.totalPoints - a.totalPoints
    }
    return a.athlete.localeCompare(b.athlete)
  })

  return rows
}

export function getCompDateColumns(
  compDates: Record<string, string>
): { compId: string; date: string }[] {
  return Object.entries(compDates)
    .map(([compId, date]) => ({ compId, date }))
    .sort((a, b) => a.date.localeCompare(b.date))
}
